import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { lowCredits } from "@/components/CreditDialog";
import { AiAssistant } from "@/components/AiAssistant";
import { MorningBriefing } from "@/components/MorningBriefing";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard,
    Users,
    Stethoscope,
    Wallet,
    Receipt,
    Building2,
    UserCog,
    LogOut,
    CalendarDays,
    FileText,
    MessageCircle,
    FileCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

const ROLE_LABEL = {
    super_admin: "Sistem Yöneticisi",
    patron: "Klinik Patronu",
    staff: "Klinik Personeli",
};

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [lowCount, setLowCount] = useState(0);
    const isClinicUser = user?.role === "patron" || user?.role === "staff";

    useEffect(() => {
        if (!isClinicUser) return;
        api.get("/clinic/credits")
            .then((r) => setLowCount(lowCredits(r.data).length))
            .catch(() => {});
    }, [isClinicUser, location.pathname]);

    const links = [];
    if (user?.role === "super_admin") {
        links.push({
            to: "/dashboard/admin",
            label: "Klinikler & Patronlar",
            icon: Building2,
            tid: "nav-admin",
        });
    } else {
        links.push({
            to: "/dashboard",
            label: "Genel Bakış",
            icon: LayoutDashboard,
            tid: "nav-overview",
            end: true,
        });
        links.push({
            to: "/dashboard/patients",
            label: "Hastalar",
            icon: Users,
            tid: "nav-patients",
        });
        links.push({
            to: "/dashboard/treatments",
            label: "Tedavi Anlaşmaları",
            icon: Stethoscope,
            tid: "nav-treatments",
        });
        links.push({
            to: "/dashboard/appointments",
            label: "Randevular",
            icon: CalendarDays,
            tid: "nav-appointments",
        });
        links.push({
            to: "/dashboard/payments",
            label: "Ödemeler",
            icon: Wallet,
            tid: "nav-payments",
        });
        links.push({
            to: "/dashboard/expenses",
            label: "Günlük Giderler",
            icon: Receipt,
            tid: "nav-expenses",
        });
        links.push({
            to: "/dashboard/reports",
            label: "Aylık Rapor",
            icon: FileText,
            tid: "nav-reports",
        });
        links.push({
            to: "/dashboard/messages",
            label: "Mesajlar & Kontür",
            icon: MessageCircle,
            tid: "nav-messages",
            badge: lowCount,
        });
        links.push({
            to: "/dashboard/invoices",
            label: "E-Faturalar",
            icon: FileCheck2,
            tid: "nav-invoices",
        });
        if (user?.role === "patron") {
            links.push({
                to: "/dashboard/staff",
                label: "Personel",
                icon: UserCog,
                tid: "nav-staff",
            });
        }
    }

    async function doLogout() {
        await logout();
        nav("/login");
    }

    return (
        <div className="min-h-screen bg-slate-50 flex">
            <aside className="w-64 bg-[#0A1218] text-white flex flex-col p-4 sticky top-0 h-screen">
                <div className="flex items-center gap-2 px-2 py-3">
                    <Logo size={36} />
                    <div>
                        <div className="font-bold tracking-tight">
                            DentDefter
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-teal-300">
                            {ROLE_LABEL[user?.role] || "-"}
                        </div>
                    </div>
                </div>
                <nav className="mt-6 flex-1 space-y-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={l.tid}
                            className={({ isActive }) =>
                                `dk-sidebar-link ${isActive ? "active" : ""}`
                            }
                        >
                            <l.icon className="w-4 h-4" />
                            {l.label}
                            {l.badge > 0 && (
                                <span
                                    className="ml-auto text-[10px] font-bold min-w-5 h-5 px-1.5 rounded-full bg-rose-500 text-white flex items-center justify-center"
                                    data-testid={`${l.tid}-low-badge`}
                                    title="Kontürü azalan kalem sayısı"
                                >
                                    {l.badge}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-white/10 pt-3">
                    <div className="px-2 pb-2">
                        <div className="text-sm font-medium truncate">
                            {user?.name}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                            {user?.email}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
                        onClick={doLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Çıkış Yap
                    </Button>
                </div>
            </aside>
            <main className="flex-1 p-8 max-w-[1400px]">
                <Outlet />
                {isClinicUser && <AiAssistant />}
                {isClinicUser && <MorningBriefing />}
            </main>
        </div>
    );
}
